const SPACE_STATUSES = new Set(['vacant', 'occupied', 'reserved', 'maintenance']);
const ALLOCATION_ACTIVE_STATUSES = new Set(['active', 'reserved']);
const WAITLIST_OPEN_STATUSES = new Set(['waiting', 'offered']);
const MOORING_PAYER_TYPES = new Set(['member', 'non_member']);
const MOORING_CADENCES = new Set(['monthly', 'annual']);
export const MOORING_CANVAS = Object.freeze({
  width: 1200,
  height: 720,
  snap: 24,
  spotWidth: 72,
  spotHeight: 48,
});

export const MOORING_CANVAS_MIN = Object.freeze({
  width: MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap * 2,
  height: MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap * 2,
});

export function normalizeMooringStatus(value, fallback = 'vacant') {
  const status = String(value || '').trim().toLowerCase();
  return SPACE_STATUSES.has(status) ? status : fallback;
}

export function isMooringsMemberVisible(modules = {}) {
  const visibility = String(modules?.sailing?.moorings?.memberVisibility || 'admin_only');
  return visibility === 'availability' || visibility === 'my_allocation';
}

export function isMooringsAvailabilityVisible(modules = {}) {
  return String(modules?.sailing?.moorings?.memberVisibility || 'admin_only') === 'availability';
}

export function isMooringsMemberMapVisible(modules = {}) {
  const moorings = modules?.sailing?.moorings || {};
  return Boolean(moorings.enabled && moorings.showMemberMap && isMooringsMemberVisible(modules));
}

export function canMooringsMemberRequestWaitlist(modules = {}) {
  const moorings = modules?.sailing?.moorings || {};
  return Boolean(moorings.enabled && moorings.allowMemberWaitlistRequests && isMooringsMemberVisible(modules));
}

export function displayNameFromProfile(profile = {}, fallback = 'Member') {
  const name = String(
    profile.name
      || profile.displayName
      || [profile.firstName, profile.lastName].filter(Boolean).join(' ')
      || profile.username
      || profile.email
      || ''
  ).trim();
  return name || fallback;
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function minorAmount(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.round(number) : 0;
}

function positiveMinorAmount(value) {
  const amount = minorAmount(value);
  return amount > 0 ? amount : 0;
}

function payerType(value) {
  const normalized = String(value || 'member').trim().toLowerCase();
  return MOORING_PAYER_TYPES.has(normalized) ? normalized : 'member';
}

function cadenceKey(value) {
  const normalized = String(value || 'annual').trim().toLowerCase();
  return MOORING_CADENCES.has(normalized) ? normalized : 'annual';
}

function cadenceAmountKey(cadence = 'annual') {
  return cadenceKey(cadence) === 'monthly' ? 'monthlyAmount' : 'annualAmount';
}

function rateAmountKey(cadence = 'annual') {
  return cadenceKey(cadence) === 'monthly' ? 'monthlyAmountPerMetre' : 'annualAmountPerMetre';
}

function squareRateAmountKey(cadence = 'annual') {
  return cadenceKey(cadence) === 'monthly' ? 'monthlyAmountPerSquareMetre' : 'annualAmountPerSquareMetre';
}

function pricingForPayer(prices = {}, type = 'member') {
  const resolvedType = payerType(type);
  return prices?.[resolvedType] || prices?.member || prices?.non_member || {};
}

function amountFromPriceGroup(prices = {}, type = 'member', cadence = 'annual') {
  return positiveMinorAmount(pricingForPayer(prices, type)?.[cadenceAmountKey(cadence)]);
}

function amountFromRateGroup(rates = {}, type = 'member', cadence = 'annual', length = null) {
  const rate = positiveMinorAmount(pricingForPayer(rates, type)?.[rateAmountKey(cadence)]);
  const billableLength = Number(length);
  if (!rate || !Number.isFinite(billableLength) || billableLength <= 0) return 0;
  return Math.round(rate * billableLength);
}

function amountFromSquareRateGroup(rates = {}, type = 'member', cadence = 'annual', squareMetres = null) {
  const rate = positiveMinorAmount(pricingForPayer(rates, type)?.[squareRateAmountKey(cadence)]);
  const billableSquareMetres = Number(squareMetres);
  if (!rate || !Number.isFinite(billableSquareMetres) || billableSquareMetres <= 0) return 0;
  return Math.round(rate * billableSquareMetres);
}

function billableSquareMetres(allocation = {}, space = {}) {
  const allocationLength = numberOrNull(allocation.length);
  const allocationBeam = numberOrNull(allocation.beam);
  if (allocationLength && allocationBeam) return allocationLength * allocationBeam;
  const spaceLength = numberOrNull(space.maxLength);
  const spaceBeam = numberOrNull(space.maxBeam);
  if (spaceLength && spaceBeam) return spaceLength * spaceBeam;
  return null;
}

export function normalizeMooringPaymentSettings(payment = {}) {
  return {
    enabled: true,
    baselineCalculation: ['flat', 'area', 'length', 'space', 'class'].includes(String(payment?.baselineCalculation || 'flat'))
      ? String(payment?.baselineCalculation || 'flat')
      : 'flat',
    cadenceMode: ['monthly', 'annual', 'payer_choice'].includes(String(payment?.cadenceMode || 'annual'))
      ? String(payment?.cadenceMode || 'annual')
      : 'annual',
    payerChoicePriceEntryMode: ['separate', 'annual', 'monthly'].includes(String(payment?.payerChoicePriceEntryMode || 'annual'))
      ? String(payment?.payerChoicePriceEntryMode || 'annual')
      : 'annual',
    stripeFeeHandling: payment?.stripeFeeHandling === 'add' ? 'add' : 'deduct',
    areaPricingMode: payment?.areaPricingMode === 'sqm' ? 'sqm' : 'fixed',
    flatPrices: payment?.flatPrices || {},
    lengthRates: payment?.lengthRates || {},
    areaPrices: payment?.areaPrices || {},
    areaRates: payment?.areaRates || {},
    boatClasses: Array.isArray(payment?.boatClasses)
      ? payment.boatClasses
        .map((boatClass) => ({
          id: String(boatClass?.id || '').trim(),
          name: String(boatClass?.name || '').trim(),
          prices: boatClass?.prices || {},
        }))
        .filter((boatClass) => boatClass.id && boatClass.name)
      : [],
  };
}

export function mooringPaymentCadences(payment = {}) {
  const settings = normalizeMooringPaymentSettings(payment);
  if (settings.cadenceMode === 'payer_choice') return ['monthly', 'annual'];
  return [settings.cadenceMode === 'monthly' ? 'monthly' : 'annual'];
}

export function resolveMooringPaymentPrice({
  payment = {},
  area = {},
  space = {},
  allocation = {},
  payerType: requestedPayerType,
  cadence = 'annual',
} = {}) {
  const settings = normalizeMooringPaymentSettings(payment);
  const resolvedPayerType = payerType(requestedPayerType || allocation.payerType);
  const resolvedCadence = cadenceKey(cadence);
  const baseline = settings.baselineCalculation;
  const selectedBoatClassId = String(allocation.boatClassId || '').trim();
  const selectedBoatClass = settings.boatClasses.find((entry) => entry.id === selectedBoatClassId);
  if (baseline === 'class' && !selectedBoatClass) {
    return {
      amount: 0,
      cadence: resolvedCadence,
      payerType: resolvedPayerType,
      source: 'none',
      stripeFeeHandling: settings.stripeFeeHandling,
    };
  }
  const customAllocationPrices = baseline === 'space' ? {} : (allocation.customPrices || allocation.mooringPrice || {});
  const allocationAmount = amountFromPriceGroup(customAllocationPrices, resolvedPayerType, resolvedCadence);
  if (allocationAmount > 0) {
    return {
      amount: allocationAmount,
      cadence: resolvedCadence,
      payerType: resolvedPayerType,
      source: 'allocation',
      ...(baseline === 'class' ? { boatClassId: selectedBoatClassId, boatClassName: selectedBoatClass.name } : {}),
      stripeFeeHandling: settings.stripeFeeHandling,
    };
  }

  const spacePrices = space.mooringPrice || {};
  const spaceAmount = amountFromPriceGroup(spacePrices, resolvedPayerType, resolvedCadence);
  if (spaceAmount > 0) {
    return {
      amount: spaceAmount,
      cadence: resolvedCadence,
      payerType: resolvedPayerType,
      source: 'space',
      ...(baseline === 'class' ? { boatClassId: selectedBoatClassId, boatClassName: selectedBoatClass.name } : {}),
      stripeFeeHandling: settings.stripeFeeHandling,
    };
  }

  if (baseline === 'area') {
    const areaId = area?.id || space?.areaId;
    if (settings.areaPricingMode === 'sqm') {
      const squareMetres = billableSquareMetres(allocation, space);
      const areaRateAmount = amountFromSquareRateGroup(settings.areaRates?.[areaId] || area?.mooringRate, resolvedPayerType, resolvedCadence, squareMetres);
      if (areaRateAmount > 0) {
        return {
          amount: areaRateAmount,
          cadence: resolvedCadence,
          payerType: resolvedPayerType,
          source: 'area_sqm',
          billableSquareMetres: squareMetres,
          stripeFeeHandling: settings.stripeFeeHandling,
        };
      }
    }
    const areaAmount = amountFromPriceGroup(settings.areaPrices?.[areaId] || area?.mooringPrice, resolvedPayerType, resolvedCadence);
    if (areaAmount > 0) {
      return {
        amount: areaAmount,
        cadence: resolvedCadence,
        payerType: resolvedPayerType,
        source: 'area',
        stripeFeeHandling: settings.stripeFeeHandling,
      };
    }
  }

  if (baseline === 'length') {
    const length = numberOrNull(allocation.length) ?? numberOrNull(space.maxLength);
    const lengthAmount = amountFromRateGroup(settings.lengthRates, resolvedPayerType, resolvedCadence, length);
    if (lengthAmount > 0) {
      return {
        amount: lengthAmount,
        cadence: resolvedCadence,
        payerType: resolvedPayerType,
        source: 'length',
        billableLength: length,
        stripeFeeHandling: settings.stripeFeeHandling,
      };
    }
  }

  if (baseline === 'class') {
    const classAmount = amountFromPriceGroup(selectedBoatClass?.prices, resolvedPayerType, resolvedCadence);
    if (classAmount > 0) {
      return {
        amount: classAmount,
        cadence: resolvedCadence,
        payerType: resolvedPayerType,
        source: 'class',
        boatClassId: selectedBoatClassId,
        boatClassName: selectedBoatClass.name,
        stripeFeeHandling: settings.stripeFeeHandling,
      };
    }
  }

  if (baseline === 'space') {
    const baselineSpaceAmount = amountFromPriceGroup(spacePrices, resolvedPayerType, resolvedCadence);
    if (baselineSpaceAmount > 0) {
      return {
        amount: baselineSpaceAmount,
        cadence: resolvedCadence,
        payerType: resolvedPayerType,
        source: 'space',
        stripeFeeHandling: settings.stripeFeeHandling,
      };
    }
  }

  const flatAmount = baseline === 'class' ? 0 : amountFromPriceGroup(settings.flatPrices, resolvedPayerType, resolvedCadence);
  if (flatAmount > 0) {
    return {
      amount: flatAmount,
      cadence: resolvedCadence,
      payerType: resolvedPayerType,
      source: 'flat',
      stripeFeeHandling: settings.stripeFeeHandling,
    };
  }

  return {
    amount: 0,
    cadence: resolvedCadence,
    payerType: resolvedPayerType,
    source: 'none',
    stripeFeeHandling: settings.stripeFeeHandling,
  };
}

function positiveNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function finiteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function canvasFromMooringArea(area = {}, fallback = MOORING_CANVAS) {
  const explicitWidth = Number(area?.canvasWidth);
  const explicitHeight = Number(area?.canvasHeight);
  if (Number.isFinite(explicitWidth) && explicitWidth > 0 && Number.isFinite(explicitHeight) && explicitHeight > 0) {
    return {
      width: Math.max(MOORING_CANVAS_MIN.width, explicitWidth),
      height: Math.max(MOORING_CANVAS_MIN.height, explicitHeight),
      snap: positiveNumber(area?.snap || fallback.snap, MOORING_CANVAS.snap),
    };
  }
  const columns = Math.max(1, Number(area?.columns || 0));
  const rows = Math.max(1, Number(area?.rows || 0));
  if (!area || (!area.rows && !area.columns)) {
    return {
      width: positiveNumber(fallback.width, MOORING_CANVAS.width),
      height: positiveNumber(fallback.height, MOORING_CANVAS.height),
      snap: positiveNumber(fallback.snap, MOORING_CANVAS.snap),
    };
  }
  return {
    width: Math.max(
      MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap * 2,
      columns * (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap) + MOORING_CANVAS.snap
    ),
    height: Math.max(
      MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap * 2,
      rows * (MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap) + MOORING_CANVAS.snap
    ),
    snap: MOORING_CANVAS.snap,
  };
}

export function gridDimensionsFromCanvas(canvas = MOORING_CANVAS) {
  const width = positiveNumber(canvas.width, MOORING_CANVAS.width);
  const height = positiveNumber(canvas.height, MOORING_CANVAS.height);
  return {
    rows: Math.max(1, Math.floor((height - MOORING_CANVAS.snap) / (MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap))),
    columns: Math.max(1, Math.floor((width - MOORING_CANVAS.snap) / (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap))),
  };
}

export function minimumCanvasForMooringSpaces(spaces = [], fallback = MOORING_CANVAS) {
  const base = canvasFromMooringArea({}, fallback);
  const unlimitedCanvas = {
    width: Math.max(base.width, 100000),
    height: Math.max(base.height, 100000),
    snap: positiveNumber(base.snap, MOORING_CANVAS.snap),
  };
  const list = Array.isArray(spaces) ? spaces : [];
  if (list.length === 0) {
    return {
      width: MOORING_CANVAS_MIN.width,
      height: MOORING_CANVAS_MIN.height,
    };
  }
  const maxRight = Math.max(...list.map((space) => {
    const visual = normalizeMooringVisual(space, unlimitedCanvas);
    return visual.x + visual.width + MOORING_CANVAS.snap;
  }));
  const maxBottom = Math.max(...list.map((space) => {
    const visual = normalizeMooringVisual(space, unlimitedCanvas);
    return visual.y + visual.height + MOORING_CANVAS.snap;
  }));
  return {
    width: Math.max(MOORING_CANVAS_MIN.width, Math.ceil(maxRight / unlimitedCanvas.snap) * unlimitedCanvas.snap),
    height: Math.max(MOORING_CANVAS_MIN.height, Math.ceil(maxBottom / unlimitedCanvas.snap) * unlimitedCanvas.snap),
  };
}

export function resizeMooringCanvas(current = MOORING_CANVAS, patch = {}, spaces = []) {
  const snap = positiveNumber(current.snap, MOORING_CANVAS.snap);
  const minimum = minimumCanvasForMooringSpaces(spaces, current);
  return {
    width: Math.max(
      minimum.width,
      MOORING_CANVAS_MIN.width,
      Math.ceil(finiteNumber(patch.width ?? current.width, current.width) / snap) * snap
    ),
    height: Math.max(
      minimum.height,
      MOORING_CANVAS_MIN.height,
      Math.ceil(finiteNumber(patch.height ?? current.height, current.height) / snap) * snap
    ),
    snap,
  };
}

export function snapMooringValue(value, snap = MOORING_CANVAS.snap) {
  const grid = positiveNumber(snap, MOORING_CANVAS.snap);
  return Math.round(finiteNumber(value, 0) / grid) * grid;
}

export function clampMooringVisual(visual = {}, canvas = MOORING_CANVAS) {
  const canvasWidth = positiveNumber(canvas.width, MOORING_CANVAS.width);
  const canvasHeight = positiveNumber(canvas.height, MOORING_CANVAS.height);
  const width = Math.min(canvasWidth, positiveNumber(visual.width, MOORING_CANVAS.spotWidth));
  const height = Math.min(canvasHeight, positiveNumber(visual.height, MOORING_CANVAS.spotHeight));
  const maxX = Math.max(0, canvasWidth - width);
  const maxY = Math.max(0, canvasHeight - height);
  return {
    ...visual,
    width,
    height,
    x: Math.min(maxX, Math.max(0, finiteNumber(visual.x, 0))),
    y: Math.min(maxY, Math.max(0, finiteNumber(visual.y, 0))),
  };
}

export function normalizeMooringVisual(space = {}, canvas = MOORING_CANVAS) {
  const hasVisualPosition = Number.isFinite(Number(space.x)) && Number.isFinite(Number(space.y));
  const width = positiveNumber(space.width, MOORING_CANVAS.spotWidth);
  const height = positiveNumber(space.height, MOORING_CANVAS.spotHeight);
  const fallbackColumn = Math.max(1, Number(space.column || 1));
  const fallbackRow = Math.max(1, Number(space.row || 1));
  const x = hasVisualPosition
    ? finiteNumber(space.x, 0)
    : (fallbackColumn - 1) * (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap);
  const y = hasVisualPosition
    ? finiteNumber(space.y, 0)
    : (fallbackRow - 1) * (MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap);
  return clampMooringVisual({
    x,
    y,
    width,
    height,
    shape: ['rect', 'circle', 'label'].includes(String(space.shape || 'rect')) ? String(space.shape || 'rect') : 'rect',
    rotation: finiteNumber(space.rotation, 0),
    labelPosition: ['inside', 'top', 'bottom'].includes(String(space.labelPosition || 'inside')) ? String(space.labelPosition || 'inside') : 'inside',
  }, canvas);
}

export function snapMooringVisual(visual = {}, snap = MOORING_CANVAS.snap, canvas = MOORING_CANVAS) {
  return clampMooringVisual({
    ...visual,
    x: snapMooringValue(visual.x, snap),
    y: snapMooringValue(visual.y, snap),
    width: snapMooringValue(positiveNumber(visual.width, MOORING_CANVAS.spotWidth), snap),
    height: snapMooringValue(positiveNumber(visual.height, MOORING_CANVAS.spotHeight), snap),
  }, canvas);
}

export function metresFromMooringVisual(visual = {}, metresPerSnap = 1, snap = MOORING_CANVAS.snap, canvas = MOORING_CANVAS) {
  const scale = positiveNumber(metresPerSnap, 1);
  const grid = positiveNumber(snap, MOORING_CANVAS.snap);
  const normalized = normalizeMooringVisual(visual, canvas);
  return {
    length: (normalized.width / grid) * scale,
    beam: (normalized.height / grid) * scale,
  };
}

export function mooringVisualFromMetres(visual = {}, dimensions = {}, metresPerSnap = 1, snap = MOORING_CANVAS.snap, canvas = MOORING_CANVAS) {
  const scale = positiveNumber(metresPerSnap, 1);
  const grid = positiveNumber(snap, MOORING_CANVAS.snap);
  const length = numberOrNull(dimensions.length);
  const beam = numberOrNull(dimensions.beam);
  return snapMooringVisual({
    ...normalizeMooringVisual(visual, canvas),
    width: length != null && length > 0 ? (length / scale) * grid : visual.width,
    height: beam != null && beam > 0 ? (beam / scale) * grid : visual.height,
  }, grid, canvas);
}

export function visualFromMooringDrag(start = {}, end = {}, options = {}) {
  const snap = positiveNumber(options.snap, MOORING_CANVAS.snap);
  const minWidth = positiveNumber(options.minWidth, MOORING_CANVAS.snap);
  const minHeight = positiveNumber(options.minHeight, MOORING_CANVAS.snap);
  const left = Math.min(finiteNumber(start.x, 0), finiteNumber(end.x, 0));
  const top = Math.min(finiteNumber(start.y, 0), finiteNumber(end.y, 0));
  const width = Math.max(minWidth, Math.abs(finiteNumber(end.x, 0) - finiteNumber(start.x, 0)));
  const height = Math.max(minHeight, Math.abs(finiteNumber(end.y, 0) - finiteNumber(start.y, 0)));
  return snapMooringVisual({
    x: left,
    y: top,
    width,
    height,
    shape: options.shape || 'rect',
    rotation: finiteNumber(options.rotation, 0),
    labelPosition: options.labelPosition || 'inside',
  }, snap, options.canvas || MOORING_CANVAS);
}

export function visualFieldsFromSpace(space = {}) {
  const visual = normalizeMooringVisual(space);
  return {
    x: visual.x,
    y: visual.y,
    width: visual.width,
    height: visual.height,
    shape: visual.shape,
    rotation: visual.rotation,
    labelPosition: visual.labelPosition,
  };
}

export function buildMooringPublicMapView(areas = [], spaces = []) {
  const safeAreas = [...(Array.isArray(areas) ? areas : [])]
    .sort((left, right) => Number(left.sortOrder || 0) - Number(right.sortOrder || 0))
    .map((area) => ({
      id: String(area.id || ''),
      name: String(area.name || 'Area'),
      type: String(area.type || 'pontoon'),
      rows: Math.max(1, Number(area.rows || 1)),
      columns: Math.max(1, Number(area.columns || 1)),
      canvasWidth: canvasFromMooringArea(area).width,
      canvasHeight: canvasFromMooringArea(area).height,
      sortOrder: Number(area.sortOrder || 0),
      canvas: canvasFromMooringArea(area),
    }));
  const areaById = new Map(safeAreas.map((area) => [area.id, area]));
  const safeSpaces = [...(Array.isArray(spaces) ? spaces : [])]
    .filter((space) => areaById.has(String(space.areaId || '')))
    .map((space) => {
      const area = areaById.get(String(space.areaId || ''));
      const visual = normalizeMooringVisual(space, area?.canvas || MOORING_CANVAS);
      return {
        id: String(space.id || ''),
        areaId: String(space.areaId || ''),
        label: String(space.label || 'Space'),
        row: Math.max(1, Number(space.row || 1)),
        column: Math.max(1, Number(space.column || 1)),
        status: visual.shape === 'label' ? 'vacant' : normalizeMooringStatus(space.status),
        maxLength: numberOrNull(space.maxLength),
        maxBeam: numberOrNull(space.maxBeam),
        maxDraft: numberOrNull(space.maxDraft),
        vesselTypes: Array.isArray(space.vesselTypes)
          ? space.vesselTypes.map((type) => String(type || '').trim()).filter(Boolean)
          : [],
        clubBoat: space.clubBoatId ? {
          label: String(space.clubBoatLabel || 'Club boat'),
        } : null,
        ...visual,
      };
    })
    .sort((left, right) => {
      const leftArea = areaById.get(left.areaId)?.sortOrder || 0;
      const rightArea = areaById.get(right.areaId)?.sortOrder || 0;
      if (leftArea !== rightArea) return leftArea - rightArea;
      if (left.row !== right.row) return left.row - right.row;
      if (left.column !== right.column) return left.column - right.column;
      return left.label.localeCompare(right.label);
    });
  return {
    areas: safeAreas,
    spaces: safeSpaces,
  };
}

export function isSpaceSuitableForVessel(space = {}, vessel = {}, options = {}) {
  if (space.shape === 'label') return { suitable: false, reasons: ['Label markers are not mooring spaces.'] };
  const requireVacant = options.requireVacant !== false;
  if (requireVacant && normalizeMooringStatus(space.status) !== 'vacant') {
    return { suitable: false, reasons: ['Space is not vacant.'] };
  }

  const reasons = [];
  const vesselLength = numberOrNull(vessel.length);
  const vesselBeam = numberOrNull(vessel.beam);
  const vesselDraft = numberOrNull(vessel.draft);
  const maxLength = numberOrNull(space.maxLength);
  const maxBeam = numberOrNull(space.maxBeam);
  const maxDraft = numberOrNull(space.maxDraft);
  const vesselType = String(vessel.type || '').trim().toLowerCase();
  const allowedTypes = Array.isArray(space.vesselTypes)
    ? space.vesselTypes.map((type) => String(type || '').trim().toLowerCase()).filter(Boolean)
    : [];

  if (vesselLength != null && maxLength != null && vesselLength > maxLength) {
    reasons.push(`Length must be <= ${maxLength}m (entered ${vesselLength}m).`);
  }
  if (vesselBeam != null && maxBeam != null && vesselBeam > maxBeam) {
    reasons.push(`Beam must be <= ${maxBeam}m (entered ${vesselBeam}m).`);
  }
  if (vesselDraft != null && maxDraft != null && vesselDraft > maxDraft) {
    reasons.push(`Draft must be <= ${maxDraft}m (entered ${vesselDraft}m).`);
  }
  if (vesselType && allowedTypes.length > 0 && !allowedTypes.includes(vesselType)) {
    reasons.push(`Vessel type must be one of: ${allowedTypes.join(', ')}.`);
  }

  if (reasons.length > 0) return { suitable: false, reasons };

  const positiveReasons = [];
  if (maxLength != null) positiveReasons.push(`Length up to ${maxLength}m`);
  if (maxBeam != null) positiveReasons.push(`Beam up to ${maxBeam}m`);
  if (maxDraft != null) positiveReasons.push(`Draft up to ${maxDraft}m`);
  if (allowedTypes.length > 0) positiveReasons.push(`Types: ${allowedTypes.join(', ')}`);
  return {
    suitable: true,
    reasons: positiveReasons.length > 0 ? positiveReasons : ['Vacant with no recorded restrictions.'],
  };
}

export function suggestMooringSpaces(spaces = [], vessel = {}) {
  return (Array.isArray(spaces) ? spaces : [])
    .map((space) => ({
      ...space,
      suitability: isSpaceSuitableForVessel(space, vessel),
    }))
    .filter((space) => space.suitability.suitable)
    .sort((left, right) => {
      const leftArea = String(left.areaName || left.areaId || '');
      const rightArea = String(right.areaName || right.areaId || '');
      if (leftArea !== rightArea) return leftArea.localeCompare(rightArea);
      const leftRow = Number(left.row || 0);
      const rightRow = Number(right.row || 0);
      if (leftRow !== rightRow) return leftRow - rightRow;
      const leftColumn = Number(left.column || 0);
      const rightColumn = Number(right.column || 0);
      if (leftColumn !== rightColumn) return leftColumn - rightColumn;
      return String(left.label || left.id || '').localeCompare(String(right.label || right.id || ''));
    });
}

export function isMooringWaitlistOpen(entry = {}) {
  return WAITLIST_OPEN_STATUSES.has(String(entry.status || 'waiting').toLowerCase());
}

export function mooringWaitlistTargetLabel(entry = {}) {
  const offeredLabel = String(entry.offeredSpaceLabel || '').trim();
  const offeredArea = String(entry.offeredAreaName || '').trim();
  if (offeredLabel) return `Offered ${[offeredArea, offeredLabel].filter(Boolean).join(' · ')}`;
  const spaceLabel = String(entry.spaceLabel || '').trim();
  const areaName = String(entry.areaName || '').trim();
  if (spaceLabel) return [areaName, spaceLabel].filter(Boolean).join(' · ');
  return 'General mooring waitlist';
}

export function mooringWaitlistSpaceMatches(entry = {}, space = {}, area = {}) {
  if (!entry || !space) return false;
  const requestedSpaceId = String(entry.spaceId || entry.offeredSpaceId || '').trim();
  if (requestedSpaceId && requestedSpaceId === String(space.id || '').trim()) return true;
  const requestedLabel = String(entry.spaceLabel || entry.offeredSpaceLabel || '').trim().toLowerCase();
  const requestedArea = String(entry.areaName || entry.offeredAreaName || '').trim().toLowerCase();
  const spaceLabel = String(space.label || '').trim().toLowerCase();
  const areaName = String(area?.name || space.areaName || '').trim().toLowerCase();
  return Boolean(requestedLabel && requestedLabel === spaceLabel && (!requestedArea || requestedArea === areaName));
}

export function waitlistQueueForSpace(entries = [], space = {}, area = {}) {
  const openEntries = sortMooringWaitlist((Array.isArray(entries) ? entries : []).filter(isMooringWaitlistOpen));
  const exact = [];
  const suitable = [];
  const other = [];
  openEntries.forEach((entry) => {
    if (mooringWaitlistSpaceMatches(entry, space, area)) {
      exact.push({ ...entry, matchType: 'requested' });
      return;
    }
    const hasRequestedSpace = Boolean(String(entry.spaceId || entry.spaceLabel || '').trim());
    const suitability = isSpaceSuitableForVessel(space, {
      type: entry.vesselType,
      length: entry.length,
      beam: entry.beam,
      draft: entry.draft,
    });
    if (!hasRequestedSpace && suitability.suitable) {
      suitable.push({ ...entry, matchType: 'suitable', suitability });
      return;
    }
    other.push({ ...entry, matchType: hasRequestedSpace ? 'other_space' : 'general', suitability });
  });
  return [...exact, ...suitable, ...other];
}

export function deriveMooringPaymentStatus(allocation = {}, memberBoltOn = null) {
  const override = String(allocation.paymentOverrideStatus || '').trim().toLowerCase();
  if (['paid', 'unpaid', 'waived', 'overdue', 'pending'].includes(override)) return override;
  const recorded = String(allocation.paymentStatus || '').trim().toLowerCase();
  if (['paid', 'unpaid', 'waived', 'overdue', 'pending', 'cancelled', 'ending'].includes(recorded)) return recorded;
  if (!allocation.boltOnId && !allocation.spaceBoltOnId) return 'not_linked';
  if (!memberBoltOn) return 'unpaid';
  if (memberBoltOn.status === 'active') return 'paid';
  if (memberBoltOn.status === 'cancel_at_period_end' || memberBoltOn.cancelAtPeriodEnd === true) {
    return 'ending';
  }
  if (memberBoltOn.manualRenewalsStopped === true || memberBoltOn.autoRenew === false) return 'ending';
  return 'unpaid';
}

export function sortMooringWaitlist(entries = []) {
  return [...(Array.isArray(entries) ? entries : [])].sort((left, right) => {
    const leftPriority = Number.isFinite(Number(left.priority)) ? Number(left.priority) : Number.MAX_SAFE_INTEGER;
    const rightPriority = Number.isFinite(Number(right.priority)) ? Number(right.priority) : Number.MAX_SAFE_INTEGER;
    if (leftPriority !== rightPriority) return leftPriority - rightPriority;
    const leftTime = toMillis(left.appliedAt || left.createdAt);
    const rightTime = toMillis(right.appliedAt || right.createdAt);
    if (leftTime !== rightTime) return leftTime - rightTime;
    return String(left.id || '').localeCompare(String(right.id || ''));
  });
}

export function summarizeMoorings({
  spaces = [],
  allocations = [],
  waitlist = [],
  maintenance = [],
  paymentStatusByAllocationId = {},
} = {}) {
  const summary = {
    total: 0,
    vacant: 0,
    occupied: 0,
    reserved: 0,
    maintenanceBlocked: 0,
    activeAllocations: 0,
    unpaidAllocations: 0,
    waitlistOpen: 0,
    maintenanceOpen: 0,
  };

  (Array.isArray(spaces) ? spaces : []).forEach((space) => {
    if (space.shape === 'label') return;
    summary.total += 1;
    const status = normalizeMooringStatus(space.status);
    if (status === 'vacant') summary.vacant += 1;
    if (status === 'occupied') summary.occupied += 1;
    if (status === 'reserved') summary.reserved += 1;
    if (status === 'maintenance') summary.maintenanceBlocked += 1;
  });

  (Array.isArray(allocations) ? allocations : []).forEach((allocation) => {
    if (ALLOCATION_ACTIVE_STATUSES.has(String(allocation.status || 'active'))) {
      summary.activeAllocations += 1;
    }
    const paymentStatus = paymentStatusByAllocationId[allocation.id] || deriveMooringPaymentStatus(allocation);
    if (['unpaid', 'overdue', 'pending'].includes(paymentStatus)) summary.unpaidAllocations += 1;
  });

  (Array.isArray(waitlist) ? waitlist : []).forEach((entry) => {
    if (WAITLIST_OPEN_STATUSES.has(String(entry.status || 'waiting'))) summary.waitlistOpen += 1;
  });

  (Array.isArray(maintenance) ? maintenance : []).forEach((entry) => {
    if (!['completed', 'cancelled'].includes(String(entry.status || 'open'))) summary.maintenanceOpen += 1;
  });

  return summary;
}

export function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}
